import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Icon } from '@/nova/components';
import { IngredientSection } from '@/nova/pages/art-style/components/IngredientSection';
import { destinationHostPath } from '@/nova/pages/creatives/tracking-link';
// The tracking-link destination sheet's own vocabulary (accordion body, page rows), so both pickers read the same.
import * as LD from '@/nova/pages/creatives/components/LinkDestinationSheet/LinkDestinationSheet.styles';
import type { DestinationOption, DestinationSelection } from '../../destinations';
import { OpenUrlButton } from '../OpenUrlButton/OpenUrlButton';
import { isOpenableHttpUrl } from '../../url-safety';
import * as S from './DestinationPicker.styles';

export type DestinationPickerProps = {
  /** The brand homepage, when the brand has a website address. */
  website: DestinationOption | null;
  /** The brand's live landing pages. */
  landing: DestinationOption[];
  landingLoading: boolean;
  /** The form's landing URL (what is saved). */
  value: string;
  selection: DestinationSelection;
  onPick: (option: DestinationOption) => void;
  /** "Custom URL" chosen: its field takes over. */
  onCustom: () => void;
  onChangeCustom: (url: string) => void;
};

/** One accordion section per kind of place. */
type SectionKind = 'website' | 'landing' | 'custom';

/**
 * "Where affiliates send players": the campaign's landing URL picked from what the brand already has instead of typed,
 * in the same accordion as an asset's tracking-link destination sheet. One section per kind of place (the website's
 * homepage, an existing landing page, a custom URL for anything else); opening a section picks its place (the page
 * last picked there, else the first), and inside the list a row picks that page. The section holding the saved URL
 * starts open.
 */
export const DestinationPicker = ({
  website,
  landing,
  landingLoading,
  value,
  selection,
  onPick,
  onCustom,
  onChangeCustom,
}: DestinationPickerProps): React.ReactElement => {
  const { t } = useTranslation();

  const allOptions = React.useMemo(() => [...(website ? [website] : []), ...landing], [website, landing]);
  const chosen = selection.kind === 'option' ? allOptions.find((option) => option.key === selection.key) : undefined;
  const selectedKind: SectionKind | null = chosen ? chosen.kind : selection.kind === 'custom' ? 'custom' : null;

  // Which section is open: the one holding the saved URL until the owner opens or closes one themselves.
  const [openOverride, setOpenOverride] = React.useState<SectionKind | null | undefined>(undefined);
  const openKind = openOverride === undefined ? selectedKind : openOverride;

  // The landing page last picked, so coming back to that section restores it.
  const [lastLanding, setLastLanding] = React.useState<string | undefined>(undefined);

  const pick = (option: DestinationOption): void => {
    if (option.kind === 'landing') setLastLanding(option.key);
    onPick(option);
  };

  /** The accordion's toggle: opening a section picks its place, closing the open one only folds it. */
  const toggle = (kind: SectionKind): void => {
    if (openKind === kind) {
      setOpenOverride(null);
      return;
    }
    setOpenOverride(kind);
    if (kind === 'custom') {
      onCustom();
      return;
    }
    if (kind === 'website') {
      if (website) pick(website);
      return;
    }
    if (selectedKind === 'landing') return;
    const first = landing.find((option) => option.key === lastLanding) ?? landing[0];
    if (first) pick(first);
  };

  const isOn = (option: DestinationOption): boolean => selection.kind === 'option' && selection.key === option.key;

  /** The section's radio dot: on for the section holding the chosen place, open or folded. */
  const radio = (kind: SectionKind): React.ReactElement => <LD.Radio $on={selectedKind === kind} aria-hidden="true" />;

  /** The landing section's header line: the picked page while closed, else how many there are. */
  const landingSummary = ((): string | undefined => {
    if (landingLoading) return undefined;
    if (chosen?.kind === 'landing' && openKind !== 'landing') return chosen.name || destinationHostPath(chosen.url);
    if (landing.length === 0) return t('None yet');
    return landing.length === 1 ? t('1 landing page') : t('{{n}} landing pages', { n: landing.length });
  })();

  const rows = (list: DestinationOption[], loading: boolean, empty: React.ReactElement): React.ReactElement => (
    <LD.PageList role="radiogroup" aria-label={t('Landing pages')}>
      {loading ? (
        <>
          <LD.PageRowSkeleton aria-hidden="true" />
          <LD.PageRowSkeleton aria-hidden="true" />
        </>
      ) : list.length === 0 ? (
        empty
      ) : (
        list.map((option) => {
          const on = isOn(option);
          const pageLabel = option.name || destinationHostPath(option.url);
          return (
            <LD.PageItem key={option.key}>
              <LD.PageRow type="button" role="radio" aria-checked={on} $selected={on} onClick={() => pick(option)}>
                <LD.Radio $on={on} aria-hidden="true" />
                <LD.Thumb aria-hidden="true">
                  {option.imageUrl ? (
                    <LD.ThumbImg src={option.imageUrl} alt="" loading="lazy" draggable={false} />
                  ) : (
                    <Icon name="globe" size={16} />
                  )}
                </LD.Thumb>
                <LD.PageCopy>
                  {option.name ? <LD.PageName raw text={option.name} /> : <LD.PageName text="Untitled landing page" />}
                  <LD.Where raw text={destinationHostPath(option.url)} />
                </LD.PageCopy>
                <LD.LinkSlot aria-hidden="true" />
              </LD.PageRow>
              {/* The area's one rule for opening a URL (as OpenUrlButton): only an absolute http(s) address. */}
              {isOpenableHttpUrl(option.url) ? (
                <S.OpenLink
                  href={option.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('Open {{name}} in a new tab', { name: pageLabel })}
                  title={t('Open {{name}} in a new tab', { name: pageLabel })}
                >
                  <Icon name="external-link" size={16} />
                </S.OpenLink>
              ) : null}
            </LD.PageItem>
          );
        })
      )}
    </LD.PageList>
  );

  return (
    <S.Root>
      <S.Label text="Where affiliates send players" tag="span" />
      <S.Well>
        <LD.Sections>
          <IngredientSection
            id="aff-dest-website"
            title={t('Straight to the website')}
            icon="home"
            leading={radio('website')}
            summary={website ? destinationHostPath(website.url) : t('No website address yet')}
            open={openKind === 'website'}
            onToggle={() => toggle('website')}
          >
            <LD.SectionBody>
              {website ? (
                <>
                  <LD.ChoiceText text="Your website's homepage" />
                  <LD.Where raw text={destinationHostPath(website.url)} />
                </>
              ) : (
                <LD.ChoiceReason text="This brand has no website address yet." />
              )}
            </LD.SectionBody>
          </IngredientSection>

          <IngredientSection
            id="aff-dest-landing"
            title={t('An existing landing page')}
            icon="document"
            leading={radio('landing')}
            summary={landingSummary}
            open={openKind === 'landing'}
            onToggle={() => toggle('landing')}
          >
            {rows(landing, landingLoading, <LD.Empty text="This brand has no landing page yet." />)}
          </IngredientSection>

          <IngredientSection
            id="aff-dest-custom"
            title={t('Custom URL')}
            icon="edit"
            leading={radio('custom')}
            summary={selectedKind === 'custom' && value.trim() ? destinationHostPath(value) : t('Type any address')}
            open={openKind === 'custom'}
            onToggle={() => toggle('custom')}
          >
            <LD.SectionBody>
              <Field
                label={t('Address')}
                value={selectedKind === 'custom' ? value : ''}
                placeholder="https://brand.com/promo"
                onChange={onChangeCustom}
                action={<OpenUrlButton url={selectedKind === 'custom' ? value : ''} />}
              />
            </LD.SectionBody>
          </IngredientSection>
        </LD.Sections>
      </S.Well>
    </S.Root>
  );
};

export default DestinationPicker;
